import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies, tvShows } from "@/mocks/media";
import MovieCard from "@/components/base/MovieCard";

type TypeFilter = "全部" | "电影" | "电视剧";
type SortFilter = "最新" | "最热" | "评分";

const typeOptions: TypeFilter[] = ["全部", "电影", "电视剧"];
const sortOptions: SortFilter[] = ["最新", "最热", "评分"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";

  const [input, setInput] = useState(keyword);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("全部");
  const [sort, setSort] = useState<SortFilter>("最新");

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useEffect(() => {
    document.title = keyword
      ? `${keyword} - 搜索结果 - 黄色影院`
      : "影视搜索 - 黄色影院";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        keyword
          ? `在黄色影院搜索“${keyword}”的相关电影与电视剧资源，查看匹配的影视作品与详情。`
          : "黄色影院影视搜索页，输入片名即可模糊匹配电影与电视剧资源，快速找到你想看的影视内容。",
      );
    }
  }, [keyword]);

  const allMedia = [...movies, ...tvShows];

  const results = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    let list = kw
      ? allMedia.filter((item) => item.title.toLowerCase().includes(kw))
      : [];

    if (typeFilter === "电影") list = list.filter((i) => i.type === "movie");
    if (typeFilter === "电视剧") list = list.filter((i) => i.type === "tv");

    if (sort === "最新") list = [...list].sort((a, b) => b.year - a.year);
    if (sort === "最热") list = [...list].sort((a, b) => b.views - a.views);
    if (sort === "评分") list = [...list].sort((a, b) => b.rating - a.rating);
    return list;
  }, [keyword, typeFilter, sort, allMedia]);

  const hot = [...allMedia].sort((a, b) => b.views - a.views).slice(0, 8);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setSearchParams(q ? { q } : {});
  };

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10 min-h-[70vh]">
      <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-6">
        影视搜索
      </h1>

      <form onSubmit={handleSubmit} className="max-w-2xl mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-2 flex-1 bg-background-100 border border-background-300 rounded-md px-3 py-2.5 focus-within:border-primary-400 transition-colors">
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-base text-foreground-500"></i>
            </span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入影片或剧集名称，如：星尘、长安"
              className="bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 outline-none w-full"
            />
          </div>
          <button
            type="submit"
            className="px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </div>
      </form>

      {keyword && (
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <p className="text-sm text-foreground-600">
            “<span className="text-primary-400 font-medium">{keyword}</span>”的搜索结果，共
            <span className="text-primary-400 font-medium"> {results.length} </span>条
          </p>
        </div>
      )}

      {keyword && (
        <div className="flex flex-wrap gap-x-8 gap-y-4 mb-8 pb-6 border-b border-background-200">
          <div className="flex items-center gap-3">
            <span className="text-sm text-foreground-500">类型</span>
            <div className="flex flex-wrap gap-2">
              {typeOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setTypeFilter(option)}
                  className={`px-3 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    typeFilter === option
                      ? "bg-primary-500 text-background-50 font-medium"
                      : "bg-background-200 text-foreground-700 hover:bg-background-300"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-foreground-500">排序</span>
            <div className="flex flex-wrap gap-2">
              {sortOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setSort(option)}
                  className={`px-3 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    sort === option
                      ? "bg-secondary-500 text-background-50 font-medium"
                      : "bg-background-200 text-foreground-700 hover:bg-background-300"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {!keyword ? (
        <div>
          <h2 className="text-lg font-semibold text-foreground-950 mb-5 flex items-center gap-2">
            <i className="ri-fire-line text-accent-400"></i>
            大家都在搜
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {hot.map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      ) : results.length === 0 ? (
        <div>
          <div className="flex flex-col items-center justify-center py-16 rounded-lg border border-dashed border-background-300 mb-10">
            <span className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 mb-4">
              <i className="ri-search-eye-line text-3xl text-foreground-500"></i>
            </span>
            <p className="text-foreground-800 mb-1">未找到相关影视</p>
            <p className="text-sm text-foreground-500 mb-5">
              换个关键词试试，或看看下面这些热门影片
            </p>
            <Link
              to="/category/movie"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-movie-2-line"></i>
              浏览电影分类
            </Link>
          </div>
          <h2 className="text-lg font-semibold text-foreground-950 mb-5 flex items-center gap-2">
            <i className="ri-fire-line text-accent-400"></i>
            热门推荐
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {hot.map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {results.map((item) => (
            <MovieCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}